// Режим «столбик». Ноль зависимостей, ноль рисования — и в этом весь смысл:
// в столбик считают там, где важна ровная разрядность, а мышью ровных
// разрядов не выходит ни на доске, ни от руки.
//
// Сетка клеток, цифры с клавиатуры: стрелки ходят по клеткам, Tab прыгает в
// надстрочный ряд переносов, «_» кладёт черту под строкой. Мышь нужна только
// чтобы поставить курсор.
//
// Сетка постоянного размера (см. Sheet), поэтому свернуть и развернуть лист
// ничего не теряет и не сдвигает: в свёрнутом виде видно окно на его середине.

import { memo, useEffect, useState } from "react";
import type { ColumnDraft } from "../../lib/task-draft";
import { Sheet } from "./Sheet";

const COLS = 48;
const ROWS = 20;
const W = 26;
const CARRY_H = 15;
const MAIN_H = 30;
const ROW_H = CARRY_H + MAIN_H;

type Cursor = { row: number; col: number; carry: boolean };

const empty = (): string[][] => Array.from({ length: ROWS }, () => Array<string>(COLS).fill(""));

/** Строка отдельным компонентом: нажатие клавиши меняет одну клетку, а без
    мемоизации перерисовывались бы все две тысячи. */
const Row = memo(function Row({
  main,
  carry,
  rule,
  cursorCol,
  cursorCarry,
  onPick,
}: {
  main: string[];
  carry: string[];
  rule: { left: number; width: number } | null;
  cursorCol: number | null;
  cursorCarry: boolean;
  onPick: (col: number, carry: boolean) => void;
}) {
  const cell = (col: number, isCarry: boolean): React.CSSProperties => ({
    width: W,
    height: isCarry ? CARRY_H : MAIN_H,
    textAlign: "center",
    fontSize: isCarry ? 11 : 20,
    lineHeight: `${isCarry ? CARRY_H : MAIN_H}px`,
    color: isCarry ? "var(--ink-4)" : "var(--ink)",
    cursor: "text",
    boxShadow: isCarry ? undefined : "inset 0 0 0 1px var(--hairline)",
    background:
      cursorCol === col && cursorCarry === isCarry ? "var(--act-bg)" : "transparent",
  });

  const pick = (col: number, isCarry: boolean) => (event: React.PointerEvent) => {
    // Фокус остаётся на листе: клавиатура принадлежит сетке целиком, клетка —
    // только место курсора.
    event.preventDefault();
    onPick(col, isCarry);
  };

  return (
    <div style={{ position: "relative", height: ROW_H }}>
      <div style={{ display: "flex", height: CARRY_H }}>
        {carry.map((value, col) => (
          <div key={col} style={cell(col, true)} onPointerDown={pick(col, true)}>
            {value}
          </div>
        ))}
      </div>
      <div style={{ display: "flex", height: MAIN_H }}>
        {main.map((value, col) => (
          <div key={col} style={cell(col, false)} onPointerDown={pick(col, false)}>
            {value}
          </div>
        ))}
      </div>
      {rule ? (
        <div
          style={{
            position: "absolute",
            left: rule.left,
            width: rule.width,
            bottom: 0,
            height: 2,
            background: "var(--ink)",
          }}
        />
      ) : null}
    </div>
  );
});

export function Column({
  visible,
  expanded,
  initialDraft,
  onChange,
}: {
  visible: boolean;
  expanded: boolean;
  initialDraft: ColumnDraft | null;
  onChange: (draft: ColumnDraft) => void;
}) {
  const [main, setMain] = useState(() => initialDraft?.main ?? empty());
  const [carry, setCarry] = useState(() => initialDraft?.carry ?? empty());
  const [rules, setRules] = useState<Set<number>>(() => new Set(initialDraft?.rules ?? []));
  // Курсор стоит у середины листа — там же, где стоит окно в свёрнутом виде.
  const [cursor, setCursor] = useState<Cursor>({ row: ROWS / 2 - 1, col: COLS / 2 - 4, carry: false });

  useEffect(
    () => onChange({ main, carry, rules: [...rules] }),
    [carry, main, onChange, rules],
  );

  const put = (value: string): void => {
    const setter = cursor.carry ? setCarry : setMain;
    setter((grid) => {
      const next = grid.map((r) => [...r]);
      next[cursor.row][cursor.col] = value;
      return next;
    });
  };

  const move = (dRow: number, dCol: number): void =>
    setCursor((c) => ({
      ...c,
      row: Math.min(ROWS - 1, Math.max(0, c.row + dRow)),
      col: Math.min(COLS - 1, Math.max(0, c.col + dCol)),
    }));

  /** Вставка символа, которого нет на клавиатуре: клетка получает его, курсор
      идёт дальше — ровно как при обычном наборе цифры. */
  const insertGlyph = (glyph: string): void => {
    put(glyph);
    move(0, 1);
  };

  const onKey = (event: React.KeyboardEvent): void => {
    const key = event.key;

    if (key === "ArrowLeft") return event.preventDefault(), move(0, -1);
    if (key === "ArrowRight") return event.preventDefault(), move(0, 1);
    if (key === "ArrowUp") return event.preventDefault(), move(-1, 0);
    if (key === "ArrowDown") return event.preventDefault(), move(1, 0);

    if (key === "Tab") {
      event.preventDefault();
      setCursor((c) => ({ ...c, carry: !c.carry }));
      return;
    }
    if (key === "Enter") {
      event.preventDefault();
      setCursor((c) => ({ ...c, row: Math.min(ROWS - 1, c.row + 1), carry: false }));
      return;
    }
    if (key === "Backspace") {
      event.preventDefault();
      put("");
      move(0, -1);
      return;
    }
    if (key === "Delete") {
      event.preventDefault();
      put("");
      return;
    }
    // √ не набрать с клавиатуры — своя горячая клавиша, как у остальных
    // спецсимволов сетки.
    if (event.altKey && key.toLowerCase() === "r") {
      event.preventDefault();
      insertGlyph("√");
      return;
    }
    // Черта под строкой — то, без чего столбик не столбик.
    if (key === "_") {
      event.preventDefault();
      setRules((set) => {
        const next = new Set(set);
        if (next.has(cursor.row)) next.delete(cursor.row);
        else next.add(cursor.row);
        return next;
      });
      return;
    }
    // Любой одиночный символ. Узкий список из цифр и знаков не пускал ни «×»,
    // ни «D» из «D = b² − 4ac» — то есть ровно то, что и пишут в черновике.
    if (key.length === 1 && !event.ctrlKey && !event.metaKey && !event.altKey) {
      event.preventDefault();
      put(key === " " ? "" : key);
      move(0, 1);
    }
  };

  /** Длина черты под строкой: от первой до последней занятой клетки этой
      строки и следующей. Черта через весь лист выглядела бы как ошибка, а не
      как столбик. */
  const ruleSpan = (r: number): { left: number; width: number } => {
    const used: number[] = [];
    for (const row of [main[r], main[r + 1] ?? []]) {
      for (let c = 0; c < COLS; c++) if (row[c]) used.push(c);
    }
    if (used.length === 0) return { left: 0, width: 6 * W };
    const from = Math.min(...used);
    const to = Math.max(...used);
    return { left: from * W, width: (to - from + 1) * W };
  };

  return (
    <>
      <Sheet
        width={COLS * W}
        height={ROWS * ROW_H}
        visible={visible}
        expanded={expanded}
        tabIndex={0}
        className="scratch__view--grid"
        onKeyDown={onKey}
        onPointerDown={(event) => event.currentTarget.focus({ preventScroll: true })}
        sheetStyle={{ fontFamily: "var(--mono)" }}
      >
        <div>
          {main.map((row, r) => (
            <Row
              key={r}
              main={row}
              carry={carry[r]}
              rule={rules.has(r) ? ruleSpan(r) : null}
              cursorCol={cursor.row === r ? cursor.col : null}
              cursorCarry={cursor.carry}
              onPick={(col, isCarry) => setCursor({ row: r, col, carry: isCarry })}
            />
          ))}
        </div>
      </Sheet>

      <div className="scratch__hint">
        <span>
          Tab — переносы и степень · «_» — черта под строкой и дроби
          {expanded ? " · Alt+R — √ · стрелки ходят по клеткам" : null}
        </span>
        <span style={{ flex: 1 }} />
        <button
          className="link link--quiet"
          onPointerDown={(event) => event.preventDefault()}
          onClick={() => insertGlyph("√")}
        >
          √
        </button>
        <button
          className="link link--quiet"
          onClick={() => {
            setMain(empty());
            setCarry(empty());
            setRules(new Set());
          }}
        >
          стереть всё
        </button>
      </div>
    </>
  );
}
